import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchKontaktInformation } from '../lib/cms';
import ContactForm from '../components/ContactForm';

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [kontaktInfo, setKontaktInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const [data, info] = await Promise.all([
        fetchPageBySlug('kontakt'),
        fetchKontaktInformation().catch(() => null)
      ]);
      setPageData(data);
      setKontaktInfo(info);
      setLoading(false);
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-dahlia-500 mb-4" />
          <p className="text-forest-900 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-12 pb-12 md:pt-20 md:pb-20">
      <Helmet>
        <title>{pageData?.meta_titel?.da || pageData?.meta_title?.da || 'Kontakt | Dahliahjørnet - H.J.K. Multiservice'}</title>
        <meta name="description" content={pageData?.meta_beskrivelse?.da || pageData?.meta_description?.da || 'Har du spørgsmål eller kommentarer? Kontakt os i dag.'} />
      </Helmet>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Contact Info & Map */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="flex flex-col gap-6">
              <h2 className="text-2xl font-serif font-bold text-forest-950">
                {pageData?.underoverskrift?.da || 'Vil du høre mere?'}
              </h2>
              <p className="text-charcoal-700 text-sm leading-relaxed">
                Hvis du har spørgsmål til vores priser, åbningstider, salg af pluk-selv-blomster, buketter eller andre varer, så er du altid velkommen til at kontakte os og høre mere. Vi står altid klar til at besvare spørgsmål fra vores kunder.
              </p>

              <ul className="flex flex-col gap-5 text-sm text-charcoal-700 border-t border-cream-100 pt-6">
                <li className="flex items-start gap-4">
                  <i className="fa-solid fa-location-dot text-xl text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                  <div>
                    <span className="font-bold text-forest-950 block mb-1">Besøg os</span>
                    <a
                      href="https://www.google.dk/maps/dir//Grindstedvej+55,+7184+Vandel"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-dahlia-600 transition-colors"
                    >
                      {kontaktInfo?.adresse || 'Grindstedvej 55'}, {kontaktInfo?.postnr_og_by || '7184 Vandel'}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <i className="fa-solid fa-phone text-lg text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                  <div>
                    <span className="font-bold text-forest-950 block mb-1">Ring til os</span>
                    <a href={`tel:${(kontaktInfo?.telefon || '22 24 39 90').replace(/\s+/g, '')}`} className="hover:text-dahlia-600 transition-colors whitespace-nowrap">
                      {kontaktInfo?.telefon || '22 24 39 90'}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <i className="fa-solid fa-envelope text-lg text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                  <div>
                    <span className="font-bold text-forest-950 block mb-1">Send en e-mail</span>
                    <a href={`mailto:${kontaktInfo?.email || 'info@dahliahjoernet.dk'}`} className="hover:text-dahlia-600 transition-colors whitespace-nowrap">
                      {kontaktInfo?.email || 'info@dahliahjoernet.dk'}
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            {/* Google Maps Embed */}
            <div className="overflow-hidden h-80 rounded-3xl">
              <iframe
                title="Dahliahjørnet på Google Maps"
                src="https://maps.google.com/maps?q=Grindstedvej%2055,%207184%20Vandel&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0 grayscale contrast-[1.1]"
                width="100%"
                height="100%"
                allowFullScreen={true}
                loading="lazy"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}
